import { Button } from "@/components/ui/button";
import { FileText, Info } from "@phosphor-icons/react";
import { toast } from "sonner";
import { CodeBlock, CodeTabs, DocSection } from "./DocComponents";

const BASE_URL = process.env.REACT_APP_BACKEND_URL;
const TRACKER_DOMAIN = process.env.REACT_APP_TRACKER_DOMAIN || "";
const TRACKER_BASE = TRACKER_DOMAIN
  ? `${TRACKER_DOMAIN.replace(/\/$/, "")}/c`
  : `${BASE_URL}/api/click`;

/**
 * Reporting API docs — the bulk of the API tab. Renders core endpoints,
 * conditional Yieldkit section, conditional Shopnomix (Amazon)
 * section, and the shared response/error/rate-limit footer.
 *
 * Receives `apiKey`, `subId`, `productsEnabled` (string[]), and an
 * `onTry` callback the parent uses to open the playground sidebar.
 */
export function ReportingApiDocs({ apiKey, subId, productsEnabled, onTry }) {
  const downloadApiDocs = async () => {
    try {
      const res = await fetch(`${BASE_URL}/api/v1/docs`, {
        headers: { "X-API-Key": apiKey },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const md = await res.text();
      const blob = new Blob([md], { type: "text/markdown" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "RedClub-Publisher-API.md";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Downloaded RedClub-Publisher-API.md");
    } catch (e) {
      toast.error(`Couldn't download docs (${e.message})`);
    }
  };

  return (
    <div className="border border-slate-200 rounded-md p-5">
      <div className="flex items-center justify-between mb-4 gap-3">
        <div className="flex items-center gap-2">
          <FileText size={18} className="text-[#002FA7]" />
          <h3 className="font-heading text-sm font-semibold text-slate-900">API Documentation</h3>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="h-8 text-xs"
          disabled={!apiKey}
          onClick={downloadApiDocs}
          data-testid="download-api-docs-btn"
          title={apiKey ? "Download a one-page Markdown reference you can share with your dev team" : "Generate an API key first"}
        >
          <FileText size={12} className="mr-1.5" /> Download .md
        </Button>
      </div>

      <div className="border border-blue-200 bg-blue-50/50 rounded-md p-3 mb-6 flex gap-2">
        <Info size={16} className="text-[#002FA7] shrink-0 mt-0.5" />
        <div className="text-xs text-slate-700">
          <p><strong>Base URL:</strong> <code className="bg-white px-1.5 py-0.5 rounded border border-slate-200 font-mono">{BASE_URL}/api</code></p>
          <p className="mt-1"><strong>Authentication:</strong> Pass your API key via <code className="bg-white px-1.5 py-0.5 rounded border border-slate-200 font-mono">X-API-Key</code> header or <code className="bg-white px-1.5 py-0.5 rounded border border-slate-200 font-mono">api_key</code> query parameter.</p>
        </div>
      </div>

      {/* GET /v1/payouts */}
      <DocSection title="GET /v1/payouts" onTry={() => onTry("payouts")}>
        <p className="text-xs text-slate-500 mb-2">One-call payouts summary: grand totals, per-brand aggregation, and per-conversion rows. Commission rates already applied — sum these directly for accounting.</p>
        <div className="text-xs text-slate-600 mb-2 space-y-1">
          <p><strong>Query Params:</strong></p>
          <ul className="list-none space-y-0.5 ml-2">
            <li><code className="bg-slate-100 px-1 rounded font-mono">brand</code> — Filter by brand name (optional)</li>
            <li><code className="bg-slate-100 px-1 rounded font-mono">start_date</code> — YYYY-MM-DD (optional)</li>
            <li><code className="bg-slate-100 px-1 rounded font-mono">end_date</code> — YYYY-MM-DD (optional)</li>
          </ul>
        </div>
        <CodeTabs snippets={{
          curl: `curl -H "X-API-Key: ${apiKey || 'YOUR_API_KEY'}" \\
  "${BASE_URL}/api/v1/payouts?start_date=2026-01-01"`,
          python: `import requests

API_KEY = "${apiKey || 'YOUR_API_KEY'}"
r = requests.get(
    "${BASE_URL}/api/v1/payouts",
    params={"start_date": "2026-01-01"},
    headers={"X-API-Key": API_KEY},
    timeout=30,
)
r.raise_for_status()
payouts = r.json()
print(payouts["totals"])         # grand totals
print(payouts["by_brand"])       # per-brand aggregation
for row in payouts["data"]:
    print(row["date"], row["brand"], "→ $", row["earning"])`,
          node: `const API_KEY = "${apiKey || 'YOUR_API_KEY'}";

const params = new URLSearchParams({ start_date: "2026-01-01" });
const res = await fetch(
  \`${BASE_URL}/api/v1/payouts?\${params}\`,
  { headers: { "X-API-Key": API_KEY } }
);
const payouts = await res.json();

console.log(payouts.totals);     // grand totals
console.log(payouts.by_brand);   // per-brand aggregation
payouts.data.forEach(r =>
  console.log(r.date, r.brand, "→ $", r.earning)
);`,
        }} />
        <p className="text-xs text-slate-500 mt-2"><strong>Response shape:</strong></p>
        <CodeBlock>{`{
  "totals": {
    "total_sale_amount": 529.97,
    "total_pub_commission": 30.30,
    "total_earning": 24.24,
    "total_conversions": 3
  },
  "by_brand": [
    { "brand": "MacPaw", "conversions": 2, "sale_amount": 379.98, "pub_commission": 22.80, "earning": 18.24 },
    { "brand": "AKKO",   "conversions": 1, "sale_amount": 149.99, "pub_commission": 7.50, "earning":  6.00 }
  ],
  "data": [
    {
      "date": "2026-04-17", "action_id": "TEST.001", "brand": "MacPaw",
      "order_id": "ORD-001", "status": "APPROVED",
      "sale_amount": 129.99, "commission_pct": 6.0,
      "pub_commission": 7.80, "earning": 6.24, "currency": "USD"
    }
  ]
}`}</CodeBlock>
      </DocSection>

      {/* GET /v1/reports/commissions */}
      <DocSection title="GET /v1/reports/commissions" onTry={() => onTry("commissions")}>
        <p className="text-xs text-slate-500 mb-2">Returns approved conversion rows. Use <code className="bg-slate-100 px-1 rounded font-mono">/v1/payouts</code> if you just need totals.</p>
        <div className="text-xs text-slate-600 mb-2 space-y-1">
          <p><strong>Query Params:</strong></p>
          <ul className="list-none space-y-0.5 ml-2">
            <li><code className="bg-slate-100 px-1 rounded font-mono">brand</code> — Filter by brand name (optional, default: all)</li>
            <li><code className="bg-slate-100 px-1 rounded font-mono">start_date</code> — YYYY-MM-DD (optional)</li>
            <li><code className="bg-slate-100 px-1 rounded font-mono">end_date</code> — YYYY-MM-DD (optional)</li>
          </ul>
        </div>
        <CodeTabs snippets={{
          curl: `curl -H "X-API-Key: ${apiKey || 'YOUR_API_KEY'}" \\
  "${BASE_URL}/api/v1/reports/commissions?start_date=2025-01-01&end_date=2026-04-17"`,
          python: `import requests

r = requests.get(
    "${BASE_URL}/api/v1/reports/commissions",
    params={"start_date": "2025-01-01", "end_date": "2026-04-17"},
    headers={"X-API-Key": "${apiKey || 'YOUR_API_KEY'}"},
    timeout=30,
)
for row in r.json()["data"]:
    print(row["date"], row["brand"], row["status"], "→ $", row["earning"])`,
          node: `const res = await fetch(
  "${BASE_URL}/api/v1/reports/commissions?start_date=2025-01-01&end_date=2026-04-17",
  { headers: { "X-API-Key": "${apiKey || 'YOUR_API_KEY'}" } }
);
const { data } = await res.json();
data.forEach(r =>
  console.log(r.date, r.brand, r.status, "→ $", r.earning)
);`,
        }} />
        <p className="text-xs text-slate-500 mt-2"><strong>Response fields:</strong> action_id, brand, status, sale_amount, commission_pct, pub_commission, earning, currency, date, order_id, promo_code. Optional <code className="bg-slate-100 px-1 rounded font-mono">pub_commission_pct</code> only when realised rate differs from assigned <code className="bg-slate-100 px-1 rounded font-mono">commission_pct</code>.</p>
      </DocSection>

      {/* GET /v1/reports/clicks */}
      <DocSection title="GET /v1/reports/clicks" onTry={() => onTry("clicks")}>
        <p className="text-xs text-slate-500 mb-2">Returns click-level performance per brand (clicks, actions, impressions, CR, EPC).</p>
        <div className="text-xs text-slate-600 mb-2 space-y-1">
          <p><strong>Query Params:</strong> (same filter shape as conversions)</p>
          <ul className="list-none space-y-0.5 ml-2">
            <li><code className="bg-slate-100 px-1 rounded font-mono">brand</code> — Exact brand name filter (optional, default: all)</li>
            <li><code className="bg-slate-100 px-1 rounded font-mono">start_date</code> — YYYY-MM-DD in your account timezone (optional)</li>
            <li><code className="bg-slate-100 px-1 rounded font-mono">end_date</code> — YYYY-MM-DD in your account timezone (optional)</li>
          </ul>
        </div>
        <CodeTabs snippets={{
          curl: `curl -H "X-API-Key: ${apiKey || 'YOUR_API_KEY'}" \\
  "${BASE_URL}/api/v1/reports/clicks?brand=MacPaw&start_date=2026-01-01&end_date=2026-01-31"`,
          python: `import requests

r = requests.get(
    "${BASE_URL}/api/v1/reports/clicks",
    params={"brand": "MacPaw", "start_date": "2026-01-01", "end_date": "2026-01-31"},
    headers={"X-API-Key": "${apiKey || 'YOUR_API_KEY'}"},
    timeout=30,
)
payload = r.json()
print("tz:", payload.get("timezone"))
for row in payload["data"]:
    print(row["brand"], "clicks:", row["clicks"], "CR:", row["cr"])`,
          node: `const params = new URLSearchParams({
  brand: "MacPaw",
  start_date: "2026-01-01",
  end_date: "2026-01-31",
});
const res = await fetch(
  \`${BASE_URL}/api/v1/reports/clicks?\${params}\`,
  { headers: { "X-API-Key": "${apiKey || 'YOUR_API_KEY'}" } }
);
const { data, timezone } = await res.json();
console.log("tz:", timezone);
data.forEach(r => console.log(r.brand, "clicks:", r.clicks, "CR:", r.cr));`,
        }} />
        <p className="text-xs text-slate-500 mt-2"><strong>Response fields:</strong> brand, clicks, actions, impressions, sale_amount, cr, epc, commission_pct — plus top-level <code className="bg-slate-100 px-1 rounded font-mono">timezone</code>.</p>
      </DocSection>

      {/* GET /v1/brands */}
      <DocSection title="GET /v1/brands" onTry={() => onTry("brands")}>
        <p className="text-xs text-slate-500 mb-2">Returns your assigned brands with tracking links (SubID already appended). One row per brand by default; any extra tracker slots you've configured live under a nested <code className="bg-slate-100 px-1 rounded font-mono">slots</code> array on the same brand object.</p>
        <div className="text-xs text-slate-600 mb-2 space-y-1">
          <p><strong>Query Params:</strong></p>
          <ul className="list-none space-y-0.5 ml-2">
            <li><code className="bg-slate-100 px-1 rounded font-mono">search</code> — case-insensitive substring on brand <em>or</em> advertiser name (e.g. <code className="bg-slate-100 px-1 rounded font-mono">?search=etsy</code>).</li>
            <li><code className="bg-slate-100 px-1 rounded font-mono">status</code> — <code className="bg-slate-100 px-1 rounded font-mono">active</code> | <code className="bg-slate-100 px-1 rounded font-mono">draft</code> | <code className="bg-slate-100 px-1 rounded font-mono">paused</code> | <code className="bg-slate-100 px-1 rounded font-mono">disabled</code>. Overrides <code className="bg-slate-100 px-1 rounded font-mono">include_draft</code> when set.</li>
            <li><code className="bg-slate-100 px-1 rounded font-mono">network</code> — case-insensitive filter on the upstream feed (<code className="bg-slate-100 px-1 rounded font-mono">impact</code>, <code className="bg-slate-100 px-1 rounded font-mono">skimlinks</code>, <code className="bg-slate-100 px-1 rounded font-mono">mrge</code>, <code className="bg-slate-100 px-1 rounded font-mono">partnerize</code>, <code className="bg-slate-100 px-1 rounded font-mono">yieldkit</code>, <code className="bg-slate-100 px-1 rounded font-mono">shopnomix</code>, <code className="bg-slate-100 px-1 rounded font-mono">cj</code>, <code className="bg-slate-100 px-1 rounded font-mono">admedia</code>, <code className="bg-slate-100 px-1 rounded font-mono">flexoffers</code>, <code className="bg-slate-100 px-1 rounded font-mono">maxbounty</code>, <code className="bg-slate-100 px-1 rounded font-mono">cashbackhive</code>, <code className="bg-slate-100 px-1 rounded font-mono">fatcoupon</code>, <code className="bg-slate-100 px-1 rounded font-mono">siteplug</code>, <code className="bg-slate-100 px-1 rounded font-mono">giddyup</code>, <code className="bg-slate-100 px-1 rounded font-mono">pubadhub</code>, <code className="bg-slate-100 px-1 rounded font-mono">pubadhub_kevin</code>, <code className="bg-slate-100 px-1 rounded font-mono">shopnomix_kevin</code>, <code className="bg-slate-100 px-1 rounded font-mono">pronomix</code>, <code className="bg-slate-100 px-1 rounded font-mono">ld</code>). Comma-separated list supported.</li>
            <li><code className="bg-slate-100 px-1 rounded font-mono">payout_mode</code> — <code className="bg-slate-100 px-1 rounded font-mono">fixed_pct</code> | <code className="bg-slate-100 px-1 rounded font-mono">passthrough</code> | <code className="bg-slate-100 px-1 rounded font-mono">flat_cpa</code>. Comma-separated list supported.</li>
            <li><code className="bg-slate-100 px-1 rounded font-mono">min_rate</code> / <code className="bg-slate-100 px-1 rounded font-mono">max_rate</code> — bounds on <code className="bg-slate-100 px-1 rounded font-mono">commission_pct_max</code>. <code className="bg-slate-100 px-1 rounded font-mono">flat_cpa</code> rows are excluded when either bound is set.</li>
            <li><code className="bg-slate-100 px-1 rounded font-mono">enabled</code> — <code className="bg-slate-100 px-1 rounded font-mono">1</code> for live brands only, <code className="bg-slate-100 px-1 rounded font-mono">0</code> for paused only.</li>
            <li><code className="bg-slate-100 px-1 rounded font-mono">include_draft</code> — <code className="bg-slate-100 px-1 rounded font-mono">0</code> or <code className="bg-slate-100 px-1 rounded font-mono">1</code> (default <code className="bg-slate-100 px-1 rounded font-mono">1</code>). Set to <code className="bg-slate-100 px-1 rounded font-mono">0</code> to omit draft brands entirely — recommended for production ingestion.</li>
            <li><code className="bg-slate-100 px-1 rounded font-mono">flatten</code> — <code className="bg-slate-100 px-1 rounded font-mono">1</code> returns the legacy flat shape (one row per slot — "Etsy", "Etsy A", "Etsy B" as siblings).</li>
            <li><code className="bg-slate-100 px-1 rounded font-mono">page</code> / <code className="bg-slate-100 px-1 rounded font-mono">limit</code> — pagination, defaults <code className="bg-slate-100 px-1 rounded font-mono">1</code> / <code className="bg-slate-100 px-1 rounded font-mono">200</code>. Hard max <code className="bg-slate-100 px-1 rounded font-mono">1000</code>.</li>
          </ul>
        </div>
        <CodeTabs snippets={{
          curl: `# Default — grouped, all assigned brands
curl -H "X-API-Key: ${apiKey || 'YOUR_API_KEY'}" \\
  "${BASE_URL}/api/v1/brands?include_draft=0"

# Filter examples
curl -H "X-API-Key: ${apiKey || 'YOUR_API_KEY'}" \\
  "${BASE_URL}/api/v1/brands?search=etsy"

curl -H "X-API-Key: ${apiKey || 'YOUR_API_KEY'}" \\
  "${BASE_URL}/api/v1/brands?network=impact,skimlinks&min_rate=3"

# Paginate at 500/page
curl -H "X-API-Key: ${apiKey || 'YOUR_API_KEY'}" \\
  "${BASE_URL}/api/v1/brands?page=2&limit=500"`,
          python: `import requests

r = requests.get(
    "${BASE_URL}/api/v1/brands",
    params={
        "include_draft": 0,
        "search": "etsy",            # substring on brand/advertiser
        "network": "mrge",           # comma-list ok: "mrge,impact"
        "min_rate": 3,               # only rows with commission_pct_max >= 3
        "limit": 500,
    },
    headers={"X-API-Key": "${apiKey || 'YOUR_API_KEY'}"},
    timeout=30,
).json()
print(r["pagination"])              # {page, limit, total_pages, has_more}
for b in r["data"]:
    print(b["brand"], "→", b["tracking_link"])
    for slot in b.get("slots", []):  # extra tracker URLs (A, B, …)
        print("  ", slot["slot_label"], slot["tracking_link"])`,
          node: `const params = new URLSearchParams({
  include_draft: "0",
  search:        "etsy",
  network:       "mrge",
  min_rate:      "3",
  limit:         "500",
});
const res = await fetch(
  \`${BASE_URL}/api/v1/brands?\${params}\`,
  { headers: { "X-API-Key": "${apiKey || 'YOUR_API_KEY'}" } }
);
const { data, pagination } = await res.json();
data.forEach(b => {
  console.log(b.brand, "→", b.tracking_link);
  (b.slots || []).forEach(s => console.log("  ", s.slot_label, s.tracking_link));
});`,
        }} />
        <div className="text-xs text-slate-600 mt-3 p-3 bg-slate-50 border border-slate-200 rounded">
          <p className="font-semibold mb-1">Response envelope</p>
          <pre className="font-mono text-[11px] leading-snug">{`{
  "data": [
    {
      "brand": "Etsy US",
      "advertiser": "Etsy",
      "tracking_link": "…?subId1=0NWQ3H",     // your main tracker
      "slots": [                                // omitted when there are none
        { "slot_label": "A", "sub_id1": "0NWQ3H-A", "tracking_link": "…" },
        { "slot_label": "B", "sub_id1": "0NWQ3H-B", "tracking_link": "…" }
      ],
      "payout_mode": "passthrough",
      "commission_pct_max": 2.45,
      "enabled": true,
      "last_checked_at": "2026-02-20T…Z"
    }
  ],
  "total": 471,
  "pagination": { "page": 1, "limit": 200, "total_pages": 3, "has_more": true }
}`}</pre>
        </div>
        <p className="text-xs text-slate-500 mt-2"><strong>Per-brand fields:</strong> brand, advertiser, commission_pct, status (<code className="bg-slate-100 px-1 rounded font-mono">active</code> | <code className="bg-slate-100 px-1 rounded font-mono">draft</code>), tracking_link, enabled, last_checked_at, slots, initial_test (<code className="bg-slate-100 px-1 rounded font-mono">{"{clicks_per_day, duration_days, min_cr_pct, notes}"}</code> — ramp test required before scale)</p>
        <div className="text-xs text-slate-600 mt-3 p-3 bg-amber-50 border border-amber-200 rounded space-y-1.5">
          <p className="font-semibold text-amber-900">What does <code className="bg-amber-100 px-1 rounded font-mono">"status": "draft"</code> mean?</p>
          <p>Draft brands are offers whose commission rate could not be parsed into a numeric value at sync time — usually a brand-new passthrough listing whose rate string is missing or non-standard.</p>
          <ul className="list-disc ml-4 space-y-0.5 text-amber-800">
            <li><strong>Safe to ignore.</strong> Treat <code className="bg-amber-100 px-1 rounded font-mono">active</code> as ingestable; skip <code className="bg-amber-100 px-1 rounded font-mono">draft</code>.</li>
            <li>Use <code className="bg-amber-100 px-1 rounded font-mono">?include_draft=0</code> to have the server filter them out before they reach you.</li>
            <li>If a brand sits in <code className="bg-amber-100 px-1 rounded font-mono">draft</code> for &gt;24h, ping your account manager — we'll surface a rate manually.</li>
          </ul>
        </div>
      </DocSection>

      {/* GET /v1/reports/clicks-log */}
      <DocSection title="GET /v1/reports/clicks-log" onTry={() => onTry("clicksLog")}>
        <p className="text-xs text-slate-500 mb-2">Same Click Log as the publisher dashboard. Each row = 1 click. Sale amount may be $0 for CPA/travel partners — <code className="bg-slate-100 px-1 rounded font-mono">commission</code> is still the USD earning from the network payout. Totals on page 1 match the dashboard tiles.</p>
        <div className="text-xs text-slate-600 mb-2 space-y-1">
          <p><strong>Query Params:</strong></p>
          <ul className="list-none space-y-0.5 ml-2">
            <li><code className="bg-slate-100 px-1 rounded font-mono">brand</code> — Filter by brand name (optional)</li>
            <li><code className="bg-slate-100 px-1 rounded font-mono">start_date</code> — YYYY-MM-DD in your account timezone (optional)</li>
            <li><code className="bg-slate-100 px-1 rounded font-mono">end_date</code> — YYYY-MM-DD in your account timezone (optional)</li>
          </ul>
          <p className="text-slate-500 mt-2">
            Dates and timestamps use your account timezone (Profile → Timezone). Missing timezone = UTC.
            New accounts default to America/New_York. Response includes top-level <code className="bg-slate-100 px-1 rounded font-mono">timezone</code>.
          </p>
        </div>
        <CodeTabs snippets={{
          curl: `curl -H "X-API-Key: ${apiKey || 'YOUR_API_KEY'}" \\
  "${BASE_URL}/api/v1/reports/clicks-log?brand=MacPaw&start_date=2026-01-01"`,
          python: `import requests

r = requests.get(
    "${BASE_URL}/api/v1/reports/clicks-log",
    params={"brand": "MacPaw", "start_date": "2026-01-01"},
    headers={"X-API-Key": "${apiKey || 'YOUR_API_KEY'}"},
    timeout=30,
)
payload = r.json()
print("tz:", payload.get("timezone"))
for c in payload["data"]:
    mark = "✓" if c["sales"] else "·"
    print(mark, c["date"][:10], c["rid"], c["status"], "→ $", c["commission"])`,
          node: `const params = new URLSearchParams({
  brand: "MacPaw",
  start_date: "2026-01-01",
});
const res = await fetch(
  \`${BASE_URL}/api/v1/reports/clicks-log?\${params}\`,
  { headers: { "X-API-Key": "${apiKey || 'YOUR_API_KEY'}" } }
);
const { data, timezone } = await res.json();
console.log("tz:", timezone);
data.forEach(c => {
  const mark = c.sales ? "✓" : "·";
  console.log(mark, c.date.slice(0, 10), c.rid, c.status, "→ $", c.commission);
});`,
        }} />
        <p className="text-xs text-slate-500 mt-2"><strong>Response fields:</strong> date, rid, brand, sales, sale_amount, pub_commission, commission/earnings (USD, including $0-sale payouts), commission_pct, payout_mode, status, action_id, order_id, promo_code, currency, event_date, sub_id1, sub_id2, sub_id3, tracker_source, network. Matches Reports → Click Log. For billing totals use conversions (one row per conversion).</p>
      </DocSection>

      {/* GET /v1/reports/conversions — RECOMMENDED for billing reconciliation */}
      <DocSection title="⭐ GET /v1/reports/conversions" onTry={() => onTry("conversions")}>
        <p className="text-xs text-slate-500 mb-2">
          <strong>Recommended for billing reconciliation.</strong> Same engine as publisher Commission Level / Payouts.
          One row per conversion (USD). Sale amount may be $0 — <code className="bg-slate-100 px-1 rounded font-mono">commission</code> is still the payout-based earning.
          Includes click side (<code className="bg-slate-100 px-1 rounded font-mono">rid</code>, <code className="bg-slate-100 px-1 rounded font-mono">click_id</code>, <code className="bg-slate-100 px-1 rounded font-mono">click_date</code>) so you can match stored click IDs.
        </p>
        <div className="text-xs text-slate-600 mb-2 space-y-1">
          <p><strong>Query Params:</strong></p>
          <ul className="list-none space-y-0.5 ml-2">
            <li><code className="bg-slate-100 px-1 rounded font-mono">brand</code> — Filter by brand name (optional)</li>
            <li><code className="bg-slate-100 px-1 rounded font-mono">start_date</code> / <code className="bg-slate-100 px-1 rounded font-mono">end_date</code> — YYYY-MM-DD (optional)</li>
            <li><code className="bg-slate-100 px-1 rounded font-mono">sub_id2</code> — Match your stored click_id exactly (optional)</li>
            <li><code className="bg-slate-100 px-1 rounded font-mono">sub_id3</code> — Optional</li>
            <li><code className="bg-slate-100 px-1 rounded font-mono">status</code> — APPROVED / PENDING / REJECTED (optional; REVERSED still accepted)</li>
            <li><code className="bg-slate-100 px-1 rounded font-mono">page</code> / <code className="bg-slate-100 px-1 rounded font-mono">page_size</code> — walk the full set (default 10000, max 50000). Sorted uniquely by conversion time + action_id so pages never skip or duplicate rows that share a timestamp.</li>
            <li><code className="bg-slate-100 px-1 rounded font-mono">cursor</code> — optional; pass the previous page's <code className="bg-slate-100 px-1 rounded font-mono">next_cursor</code></li>
          </ul>
        </div>
        <CodeTabs snippets={{
          curl: `curl -H "X-API-Key: ${apiKey || 'YOUR_API_KEY'}" \\
  "${BASE_URL}/api/v1/reports/conversions?status=APPROVED&start_date=2026-04-01"`,
          python: `import requests

r = requests.get(
    "${BASE_URL}/api/v1/reports/conversions",
    params={"status": "APPROVED", "start_date": "2026-04-01"},
    headers={"X-API-Key": "${apiKey || 'YOUR_API_KEY'}"},
    timeout=30,
)
for c in r.json()["data"]:
    print(c["click_id"], "->", c["brand"], "$", c["commission"], "(", c["status"], ")")`,
          node: `const params = new URLSearchParams({
  status: "APPROVED",
  start_date: "2026-04-01",
});
const res = await fetch(
  \`${BASE_URL}/api/v1/reports/conversions?\${params}\`,
  { headers: { "X-API-Key": "${apiKey || 'YOUR_API_KEY'}" } }
);
const { data } = await res.json();
data.forEach(c => {
  console.log(c.click_id, "->", c.brand, "$", c.commission, "(", c.status, ")");
});`,
        }} />
        <p className="text-xs text-slate-500 mt-2">
          <strong>Response fields:</strong> rid, click_id, click_date, sub_id2, sub_id3, action_id, conversion_date, brand, sale_amount, pub_commission, commission (earning), currency, status (APPROVED / PENDING / REJECTED — same as dashboard Commission Level CSV), status_label, order_id, promo_code, network. Concatenated pages = dashboard Commission Level CSV.
        </p>
      </DocSection>

      {productsEnabled.includes("cpc") && <YieldkitDocs apiKey={apiKey} subId={subId} />}
      {productsEnabled.includes("shopnomix") && <ShopnomixDocs apiKey={apiKey} subId={subId} />}

      {/* Response Format */}
      <DocSection title="Response Format">
        <p className="text-xs text-slate-500 mb-2">List endpoints return <code className="bg-slate-100 px-1 rounded font-mono">{"{ data: [...], total: N }"}</code>. The <code className="bg-slate-100 px-1 rounded font-mono">/v1/payouts</code> endpoint returns a richer shape with <code className="bg-slate-100 px-1 rounded font-mono">totals</code>, <code className="bg-slate-100 px-1 rounded font-mono">by_brand</code>, and <code className="bg-slate-100 px-1 rounded font-mono">data</code> (see its section above).</p>
        <CodeBlock>{`{
  "data": [ ... ],   // Array of records
  "total": 42        // Total count
}`}</CodeBlock>
      </DocSection>

      {/* Error Codes */}
      <DocSection title="Error Codes">
        <div className="border border-slate-200 rounded-md overflow-hidden">
          <table className="w-full text-xs">
            <thead><tr className="bg-slate-50 border-b border-slate-200">
              <th className="text-left px-3 py-2 font-bold text-slate-500">Code</th>
              <th className="text-left px-3 py-2 font-bold text-slate-500">Meaning</th>
            </tr></thead>
            <tbody>
              <tr className="border-b border-slate-100"><td className="px-3 py-2 font-mono">200</td><td className="px-3 py-2 text-slate-600">Success</td></tr>
              <tr className="border-b border-slate-100"><td className="px-3 py-2 font-mono">401</td><td className="px-3 py-2 text-slate-600">Missing or invalid API key</td></tr>
              <tr><td className="px-3 py-2 font-mono">500</td><td className="px-3 py-2 text-slate-600">Server error</td></tr>
            </tbody>
          </table>
        </div>
      </DocSection>

      {/* Rate Limits */}
      <DocSection title="Rate Limits">
        <p className="text-xs text-slate-500">60 requests per minute per API key. Exceeding this will return HTTP 429.</p>
      </DocSection>
    </div>
  );
}

/* ─────────── YIELDKIT docs (conditional) ─────────── */
function YieldkitDocs({ apiKey, subId }) {
  return (
    <>
      <div className="pt-4 mt-6 border-t-2 border-[#002FA7]/20">
        <div className="flex items-center gap-2 mb-2">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[10px] font-bold tracking-[0.2em] uppercase bg-[#002FA7] text-white">
            ⚡ Yieldkit
          </span>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold tracking-wider text-amber-900 bg-amber-100 border border-amber-200">EUR</span>
        </div>
        <h2 className="font-heading text-xl font-bold text-slate-900 mt-2">Yieldkit endpoints</h2>
        <p className="text-xs text-slate-500">Affiliate programs from Yieldkit. Tracker host: <code className="bg-slate-100 px-1 rounded font-mono">offers.goshoptoday.com</code>. Pass-through commissions (EUR).</p>
      </div>

      <DocSection title="GET /v1/cpc/brands">
        <p className="text-xs text-slate-500 mb-2">Paginated Yieldkit catalog. Filters: <code className="font-mono bg-slate-100 px-1 rounded">country</code>, <code className="font-mono bg-slate-100 px-1 rounded">category</code>, <code className="font-mono bg-slate-100 px-1 rounded">search</code>, <code className="font-mono bg-slate-100 px-1 rounded">page</code>, <code className="font-mono bg-slate-100 px-1 rounded">page_size</code> (max 200), <code className="font-mono bg-slate-100 px-1 rounded">sort</code> (<code>name</code>, <code>-name</code>, <code>ecpc</code>, <code>-ecpc</code>).</p>
        <CodeTabs snippets={{
          curl: `curl -H "X-API-Key: ${apiKey || 'YOUR_API_KEY'}" \\
  "${BASE_URL}/api/v1/cpc/brands?country=US&page=1&page_size=50&sort=-ecpc"`,
          python: `import requests

r = requests.get(
    "${BASE_URL}/api/v1/cpc/brands",
    params={"country": "US", "page_size": 50, "sort": "-ecpc"},
    headers={"X-API-Key": "${apiKey || 'YOUR_API_KEY'}"},
)
body = r.json()
for b in body["data"]:
    print(f"{b['name']:<30} eCPC €{b['ecpc']:.3f}  {b['tracking_link']}")`,
          node: `const res = await fetch(\`${BASE_URL}/api/v1/cpc/brands?country=US&sort=-ecpc\`, {
  headers: { "X-API-Key": "${apiKey || 'YOUR_API_KEY'}" }
});
const { data, total } = await res.json();
console.log(\`\${total} Yieldkit advertisers available\`);`,
        }} />
        <p className="text-xs text-slate-500 mt-2"><strong>Response fields:</strong> advertiser_id, name, description, url, domain, image, country, countries, category, categories, offer_type, ecpc, tracking_link, yk_raw. Plus top-level <code>total</code>, <code>page</code>, <code>page_size</code>.</p>
      </DocSection>

      <DocSection title="Tracking link anatomy">
        <p className="text-xs text-slate-500 mb-2">Every brand's <code className="font-mono bg-slate-100 px-1 rounded">tracking_link</code> is pre-stamped with your <code>subId1</code>. Append your own <code>subId2</code> and <code>subId3</code> for campaign tracking.</p>
        <CodeBlock>{`${TRACKER_BASE.replace('gadgetreview', 'goshoptoday')}/c/yk/{advertiser_id}?subId1=${subId}&subId2=fb_spring_sale&subId3=banner_728x90`}</CodeBlock>
        <p className="text-xs text-slate-500 mt-1">Our server mints a unique <code>yk_tag</code> (click ID) and 302s to Yieldkit, who 302s to the advertiser. Click is logged instantly in <code>/v1/cpc/clicks</code>.</p>
      </DocSection>

      <DocSection title="⭐ GET /v1/cpc/reports/clicks-log">
        <p className="text-xs text-slate-500 mb-2"><b>Single source of truth</b> — every click joined with its commission (if any) in one row. 100% pass-through (no split math). All EUR.</p>
        <CodeTabs snippets={{
          curl: `curl -H "X-API-Key: ${apiKey || 'YOUR_API_KEY'}" \\
  "${BASE_URL}/api/v1/cpc/reports/clicks-log?page_size=100"`,
          python: `import requests

r = requests.get(
    "${BASE_URL}/api/v1/cpc/reports/clicks-log",
    params={"page_size": 100},
    headers={"X-API-Key": "${apiKey || 'YOUR_API_KEY'}"},
)
for c in r.json()["data"]:
    mark = "✓" if c["sales"] else "·"
    print(mark, c["date"][:19], c["advertiser_name"], "→ €", c["commission"], c["status"])`,
          node: `const res = await fetch(\`${BASE_URL}/api/v1/cpc/reports/clicks-log\`, {
  headers: { "X-API-Key": "${apiKey || 'YOUR_API_KEY'}" }
});
const { data } = await res.json();
data.forEach(c => console.log(c.date, c.advertiser_name, '€' + c.commission, c.status));`,
        }} />
        <p className="text-xs text-slate-500 mt-2"><strong>Response fields:</strong> date, rid, advertiser_id, advertiser_name, sales, sale_amount, commission, currency, status (<code>click</code> · <code>OPEN</code> · <code>CONFIRMED</code> · <code>REJECTED</code> · <code>DELAYED</code>), event_id, commission_id, event_type, sales_date, modified_date, sub_id2, sub_id3.</p>
      </DocSection>

      <DocSection title="GET /v1/cpc/clicks">
        <p className="text-xs text-slate-500 mb-2">Raw click log (no commission join). Filters: <code className="font-mono bg-slate-100 px-1 rounded">advertiser_id</code>, <code className="font-mono bg-slate-100 px-1 rounded">sub_id2</code>, <code className="font-mono bg-slate-100 px-1 rounded">sub_id3</code>, <code className="font-mono bg-slate-100 px-1 rounded">start_date</code>, <code className="font-mono bg-slate-100 px-1 rounded">end_date</code>.</p>
        <CodeBlock>{`${BASE_URL}/api/v1/cpc/clicks?page_size=100`}</CodeBlock>
      </DocSection>

      <DocSection title="GET /v1/cpc/commissions">
        <p className="text-xs text-slate-500 mb-2">Commission settlement log. Filters include <code className="font-mono bg-slate-100 px-1 rounded">state</code> (<code>OPEN</code>, <code>CONFIRMED</code>, <code>REJECTED</code>, <code>DELAYED</code>).</p>
        <CodeBlock>{`${BASE_URL}/api/v1/cpc/commissions?state=CONFIRMED&start_date=2026-04-01`}</CodeBlock>
        <p className="text-xs text-slate-500 mt-2"><strong>States:</strong> <code>OPEN</code> — first reported · <code>DELAYED</code> — locked during merchant review · <code>CONFIRMED</code> — payable · <code>REJECTED</code> — voided.</p>
      </DocSection>

      <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 space-y-1">
        <p><strong>Heads up:</strong> Yieldkit settles in EUR only. Your postback URL (configured on the Integrations tab) fires for every commission event with a <code className="bg-amber-100 px-1 rounded font-mono">{'{commission}'}</code> macro in EUR. No currency conversion — store the EUR amount as-is, convert at your end if needed.</p>
      </div>
    </>
  );
}

/* ─────────── AMAZON / SHOPNOMIX docs (conditional) ─────────── */
function ShopnomixDocs({ apiKey, subId }) {
  return (
    <>
      <div className="pt-4 mt-6 border-t-2 border-orange-500/30">
        <div className="flex items-center gap-2 mb-2">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[10px] font-bold tracking-[0.2em] uppercase bg-orange-500 text-white">
            🛒 Amazon · EPC
          </span>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold tracking-wider text-emerald-900 bg-emerald-100 border border-emerald-200">USD</span>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold tracking-wider text-amber-900 bg-amber-100 border border-amber-200">T+24h confirm</span>
        </div>
        <h2 className="font-heading text-xl font-bold text-slate-900 mt-2">Amazon endpoints</h2>
        <p className="text-xs text-slate-500">
          Amazon-only EPC feed powered by Shopnomix. Tracker host:{" "}
          <code className="bg-slate-100 px-1 rounded font-mono">deals.goshoptoday.com</code>.
          Your earnings are finalized after Shopnomix confirms each day's revenue (typically T+24h).
          You see a Pending state in your report until that happens.
        </p>
      </div>

      <DocSection title="Tracking link">
        <p className="text-xs text-slate-500 mb-2">
          Mirrors Shopnomix's native <code className="font-mono bg-slate-100 px-1 rounded">/v1/bid/redirect</code> contract — pass your own <code className="font-mono bg-slate-100 px-1 rounded">cid</code> (unique click ID) and <code className="font-mono bg-slate-100 px-1 rounded">source</code> (traffic group) exactly like you would on a direct Shopnomix integration. We log every hit on our side so you see live volume in your portal and via <code className="font-mono bg-slate-100 px-1 rounded">/v1/reports/amazon/clicks</code>.
        </p>
        <CodeBlock>{`https://deals.goshoptoday.com/g/${subId || 'YOUR_SUBID'}?url=https://amazon.com&cid=UNIQUE_YOUR_CLICK_ID&source=homepage

# Minimum (url/cid/source all optional):
https://deals.goshoptoday.com/g/${subId || 'YOUR_SUBID'}`}</CodeBlock>
        <p className="text-xs text-slate-500 mt-2">
          <strong>Params:</strong> <code className="font-mono bg-slate-100 px-1 rounded">url</code> = landing URL (defaults to <code className="font-mono bg-slate-100 px-1 rounded">https://amazon.com</code> if omitted) · <code className="font-mono bg-slate-100 px-1 rounded">cid</code> = your unique click ID · <code className="font-mono bg-slate-100 px-1 rounded">source</code> = your traffic source. All three are passed through to Shopnomix as-is.
        </p>
      </DocSection>

      <DocSection title="Payout rate card">
        <p className="text-xs text-slate-500 mb-3">
          Your per-click EPC maps to the quality of traffic you send. The tier is recomputed daily once your clicks + sales confirm.
        </p>
        <div className="border border-slate-200 rounded-md overflow-hidden text-xs">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr className="border-b border-slate-200">
                <th className="text-left px-3 py-2 font-bold text-slate-500">Traffic quality (CR)</th>
                <th className="text-left px-3 py-2 font-bold text-slate-500">Your EPC</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-slate-100"><td className="px-3 py-2">5% – 15%</td><td className="px-3 py-2 font-mono">$0.10 – $0.15</td></tr>
              <tr className="border-b border-slate-100"><td className="px-3 py-2">16% – 21%</td><td className="px-3 py-2 font-mono">$0.15 – $0.18</td></tr>
              <tr className="border-b border-slate-100"><td className="px-3 py-2">22% – 30%</td><td className="px-3 py-2 font-mono">$0.18 – $0.23</td></tr>
              <tr><td className="px-3 py-2">31%+</td><td className="px-3 py-2 font-mono">$0.27</td></tr>
            </tbody>
          </table>
        </div>
      </DocSection>

      <DocSection title="GET /v1/reports/amazon/feed">
        <p className="text-xs text-slate-500 mb-2">
          Your assigned campaign + tracker URL pattern + payout model. Also available
          at <code className="font-mono bg-slate-100 px-1 rounded">/v1/shopnomix/feed</code>.
        </p>
        <CodeTabs snippets={{
          curl: `curl -H "X-API-Key: ${apiKey || 'YOUR_API_KEY'}" \\
  "${BASE_URL}/api/v1/reports/amazon/feed"`,
          python: `import requests

r = requests.get(
    "${BASE_URL}/api/v1/reports/amazon/feed",
    headers={"X-API-Key": "${apiKey || 'YOUR_API_KEY'}"},
)
print(r.json()["brand"]["tracker_url"])`,
          node: `const res = await fetch(\`${BASE_URL}/api/v1/reports/amazon/feed\`, {
  headers: { "X-API-Key": "${apiKey || 'YOUR_API_KEY'}" }
});
const { brand } = await res.json();
console.log(brand.tracker_url);`,
        }} />
      </DocSection>

      <DocSection title="⭐ GET /v1/reports/amazon/stats">
        <p className="text-xs text-slate-500 mb-2">
          Per-day breakdown with your clicks, confirmed clicks, EPC, and earnings.
          Un-confirmed days show <code className="font-mono bg-slate-100 px-1 rounded">status: "pending"</code>
          with <code className="font-mono bg-slate-100 px-1 rounded">pub_epc: null</code>; they
          populate on the next 6h Shopnomix sync.
        </p>
        <CodeTabs snippets={{
          curl: `curl -H "X-API-Key: ${apiKey || 'YOUR_API_KEY'}" \\
  "${BASE_URL}/api/v1/reports/amazon/stats?start_date=2026-04-01&end_date=2026-04-30"`,
          python: `import requests

r = requests.get(
    "${BASE_URL}/api/v1/reports/amazon/stats",
    params={"start_date": "2026-04-01", "end_date": "2026-04-30"},
    headers={"X-API-Key": "${apiKey || 'YOUR_API_KEY'}"},
)
body = r.json()
print(f"30d earnings: $" + str(body["total_earnings"]))
for d in body["days"]:
    mark = "✓" if d["status"] == "confirmed" else "…"
    epc = f"\${d['pub_epc']:.2f}" if d["pub_epc"] is not None else "pending"
    print(f"{mark} {d['date']}  clicks={d['clicks']:>4}  epc={epc}")`,
          node: `const res = await fetch(\`${BASE_URL}/api/v1/reports/amazon/stats\`, {
  headers: { "X-API-Key": "${apiKey || 'YOUR_API_KEY'}" }
});
const body = await res.json();
console.log(\`30d earnings: $\${body.total_earnings}\`);
body.days.forEach(d => {
  const mark = d.status === 'confirmed' ? '✓' : '…';
  console.log(mark, d.date, 'clicks=' + d.clicks, 'epc=' + (d.pub_epc ?? 'pending'));
});`,
        }} />
        <p className="text-xs text-slate-500 mt-2">
          <strong>Response fields:</strong> start_date, end_date, total_clicks, total_feed_clicks,
          total_earnings, currency, pending_days, confirmed_days, days[] with{" "}
          <code>date, clicks, feed_clicks, pub_epc, earnings, status</code> (<code>pending</code> · <code>confirmed</code> · <code>no-traffic</code>) and{" "}
          <code className="font-mono bg-slate-100 px-1 rounded">by_domain[]</code>{" "}
          (per-locale Amazon split: <code>root_domain, clicks, revenue</code> — empty array on pending days).
          Window-total <code className="font-mono bg-slate-100 px-1 rounded">domains[]</code> rollup is inlined too.
        </p>
        <div className="text-xs text-slate-600 mt-3 p-3 bg-blue-50 border border-blue-200 rounded space-y-1.5">
          <p className="font-semibold text-blue-900">Per-locale split (<code className="font-mono bg-blue-100 px-1 rounded">by_domain</code> + <code className="font-mono bg-blue-100 px-1 rounded">domains</code>)</p>
          <p className="text-blue-800">
            Amazon clicks split across international storefronts (<code className="bg-blue-100 px-1 rounded font-mono">amazon.com</code>,
            <code className="bg-blue-100 px-1 rounded font-mono ml-1">amazon.de</code>,
            <code className="bg-blue-100 px-1 rounded font-mono ml-1">amazon.es</code>,
            <code className="bg-blue-100 px-1 rounded font-mono ml-1">amazon.it</code>, …). Synced from Shopnomix every 6h.
            Totals sum-match the day's <code className="bg-blue-100 px-1 rounded font-mono">clicks</code>/<code className="bg-blue-100 px-1 rounded font-mono">earnings</code> — use this to catch fraud signals or build locale-level dashboards in one call.
          </p>
        </div>
      </DocSection>

      <DocSection title="GET /v1/reports/amazon/domains">
        <p className="text-xs text-slate-500 mb-2">
          Window-wide per-locale rollup. Same shape as the inlined{" "}
          <code className="font-mono bg-slate-100 px-1 rounded">domains[]</code>{" "}
          field on <code className="font-mono bg-slate-100 px-1 rounded">/v1/reports/amazon/stats</code> — use this standalone endpoint
          if you only need the locale split and want to skip the daily array.
        </p>
        <CodeBlock>{`${BASE_URL}/api/v1/reports/amazon/domains?start_date=2026-04-01&end_date=2026-04-30`}</CodeBlock>
        <p className="text-xs text-slate-500 mt-2">
          <strong>Response fields:</strong>{" "}
          <code>start_date, end_date, total_clicks, total_revenue, domains[]</code> where each item has{" "}
          <code>root_domain, clicks, revenue, share_pct, days</code>.
        </p>
      </DocSection>

      <DocSection title="GET /v1/reports/amazon/clicks">
        <p className="text-xs text-slate-500 mb-2">
          Raw click log — live (no T+24h delay).
          Filters: <code className="font-mono bg-slate-100 px-1 rounded">start_date</code>,{" "}
          <code className="font-mono bg-slate-100 px-1 rounded">end_date</code>,{" "}
          <code className="font-mono bg-slate-100 px-1 rounded">page</code>,{" "}
          <code className="font-mono bg-slate-100 px-1 rounded">page_size</code>.
          Each row includes a derived <code className="font-mono bg-slate-100 px-1 rounded">root_domain</code>{" "}
          (e.g. <code className="font-mono bg-slate-100 px-1 rounded">amazon.com</code>,
          <code className="font-mono bg-slate-100 px-1 rounded ml-1">amazon.de</code>) parsed from the deeplink.
        </p>
        <CodeBlock>{`${BASE_URL}/api/v1/reports/amazon/clicks?page_size=100`}</CodeBlock>
      </DocSection>

      <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-900 space-y-2">
        <p><strong>What you see:</strong> <code className="bg-emerald-100 px-1 rounded font-mono">pub_epc</code> and your <code className="bg-emerald-100 px-1 rounded font-mono">earnings</code>. That's your full contract with us.</p>
        <p><strong>Timing:</strong> Earnings for today + yesterday are usually in <code className="bg-emerald-100 px-1 rounded font-mono">status: "pending"</code> until Shopnomix closes the books. Clicks count shows live throughout the day.</p>
      </div>
    </>
  );
}
